// The sales lists' CSV EXPORTS (spec 09 §4.1 customers, §4.5 quotes, §4.7
// orders; D-248, P3-D PR-D5 / D-289 rulings 1, 3, 13). Pure: the serializers,
// the provenance ("honesty") row's phrases, the file name and the filter
// summaries. The walk and the download live in components/export-csv.tsx.
//
// HAND-ENUMERATED, NEVER Object.keys(row). Each list names its columns here,
// as keys of its list-item schema, and a serializer reads exactly those keys.
// A §7-redacted key is ABSENT from the payload, so it cannot reach the file by
// construction — and a key the server added tomorrow (or one smuggled onto a
// row by a bug) is not written either, because nothing here iterates the row.
// That second half is the one D-248 exists for: a file that follows the
// payload's shape leaks the day the payload grows.
//
// THE OMITTABLE MONEY COLUMN. `total_minor` (orders, quotes),
// `credit_limit_minor` (customers) and a deal's `expected_value_minor` and
// `weighted_value_minor` (P3-E2 PR E2-1, spec 09 §4.14) are §7 omissions. Such a column is written
// only when at least one row carries the key — never an empty or locked
// column — and a row without it in a written column gets an EMPTY cell (§4.1's
// "nothing, never an em dash"; the keys are optional, never nullable, so an
// empty cell cannot be misread as a null). When the column is dropped the
// provenance row says money was withheld, so "withheld" never reads as
// "forgotten".
//
// ONE DERIVED COLUMN. The customers file's `tags` (P3-E2 PR E2-2a1, D-323
// ruling 6) is the row's tag names joined by `; `, the separator the customers
// import lane splits on (spec 09 §3.15), because a list of objects is no CSV
// cell. A tag name never holds `;` (§2.27), so the join is reversible. It is
// still read from one named list-item key (`tags`), never from the row's
// shape, and archived tags are written like active ones, because the customer
// carries them all the same.
//
// CELLS ARE WIRE VALUES. Money is the integer `_minor` value beside a currency
// column (the one number format every spreadsheet locale reads the same way —
// a Russian-locale Excel reads "45000.00" as text), instants are ISO, enums are
// raw, booleans are `true`/`false`, headers are the wire key names. Only the
// provenance row is translated.

import type { TFunction } from "i18next";
import { honestyRow, toCsv, type CsvConvention } from "../../../../lib/csv";
import { lineageCodesOf, type RoleInput } from "../../../../lib/role-codes";
import { SALES_ROLE_GRANTS } from "../hooks/permissions";
import type { Quote, SalesOrder } from "../hooks/quote-queries";
import type { components } from "@assessly/api-client";
import type { Opportunity } from "../hooks/opportunity-queries";

/** A value a list-item key may hold that a CSV cell can say. */
type Scalar = string | number | boolean | null | undefined;

/** The keys of `T` whose values are scalars — so a nested object (an address)
 * cannot be named as a column and printed as "[object Object]". */
type ScalarKey<T> = {
  [K in keyof T]-?: T[K] extends Scalar ? K : never;
}[keyof T] &
  string;

interface WireColumn<T> {
  readonly key: ScalarKey<T>;
  /** A §7 omission: written only when at least one row carries the key. */
  readonly omittable?: true;
  readonly derive?: never;
}

/** A column whose cell is computed from one list-item key that is not a
 * scalar. Never omittable: it carries no money. */
interface DerivedColumn<T> {
  readonly key: string;
  readonly omittable?: never;
  readonly derive: (row: T) => Scalar;
}

type ExportColumn<T> = WireColumn<T> | DerivedColumn<T>;

/** A customer as the book's list read serves it (`CustomerWithNextStep`):
 * the customer with its next step, its tags and the live-business flag. */
export type CustomerListRow = components["schemas"]["CustomerWithNextStep"];

/** The separator a customer's tag names are joined with, the one the
 * customers import lane splits on (spec 09 §3.15). */
export const TAG_NAME_SEPARATOR = "; ";

/** A row's tag names as one cell, in the order the wire serves them (the
 * lower-cased name, then the id). An untagged customer gets an empty cell. */
export function tagNamesCell(row: Pick<CustomerListRow, "tags">): string {
  return row.tags.map((tag) => tag.name).join(TAG_NAME_SEPARATOR);
}

/** A serialized list: the header (wire key names), the body rows, and whether
 * an omittable money column was dropped because no row carried it. */
export interface SerializedList {
  readonly header: readonly string[];
  readonly body: readonly (readonly (string | number | null | undefined)[])[];
  readonly moneyOmitted: boolean;
}

/**
 * §4.7's orders. `promise_date` and `at_risk_reason` joined with P3-J J-3,
 * once module 03's promise watch made them real (the reason names why the
 * order is at risk, so the boolean `at_risk` beside it would only repeat it).
 * `lines_total` / `lines_reserved` are the floor ship queue's badge, not an
 * order fact. `customer_id` is left out too: the row names its customer by
 * `customer_display_name`.
 */
export const ORDER_EXPORT_COLUMNS: readonly ExportColumn<SalesOrder>[] = [
  { key: "order_no" },
  { key: "customer_display_name" },
  { key: "customer_po_ref" },
  { key: "status" },
  { key: "status_at" },
  { key: "credit_check_status" },
  { key: "currency" },
  { key: "total_minor", omittable: true },
  { key: "confirmed_at" },
  { key: "promise_date" },
  { key: "at_risk_reason" },
  { key: "created_at" },
  { key: "owner_user_id" },
  { key: "fulfilling_facility_id" },
  { key: "quote_id" },
  { key: "id" },
];

/** §4.5's quotes — every scalar the list row carries except two:
 * `current_revision_no` (`revision_count` states the revisions, and
 * `sent_revision_no` is written) and `customer_id` (the row names its customer
 * by `customer_display_name`). */
export const QUOTE_EXPORT_COLUMNS: readonly ExportColumn<Quote>[] = [
  { key: "quote_no" },
  { key: "customer_display_name" },
  { key: "customer_tier" },
  { key: "status" },
  { key: "origin" },
  { key: "currency" },
  { key: "total_minor", omittable: true },
  { key: "valid_until" },
  { key: "revision_count" },
  { key: "sent_revision_no" },
  { key: "has_off_list_line" },
  { key: "has_margin_breach" },
  { key: "first_viewed_at" },
  { key: "last_viewed_at" },
  { key: "owner_user_id" },
  { key: "created_at" },
  { key: "updated_at" },
  { key: "id" },
];

/** §4.1's customers. The two nested addresses are not flat values, and the
 * free-text `notes` is left out (it became a timeline in E2-2a1, and the
 * alias carries only the pinned note's words). The credit limit's currency is
 * the customer's `default_currency`. `tags` is the one derived column. */
export const CUSTOMER_EXPORT_COLUMNS: readonly ExportColumn<CustomerListRow>[] = [
  { key: "customer_no" },
  { key: "display_name" },
  { key: "legal_name" },
  { key: "tax_id" },
  { key: "country" },
  { key: "tier" },
  { key: "default_currency" },
  { key: "credit_limit_minor", omittable: true },
  { key: "credit_terms_days" },
  { key: "credit_hold" },
  { key: "reliability_score" },
  { key: "reliability_provisional" },
  { key: "preferred_channel" },
  { key: "preferred_locale" },
  { key: "owner_user_id" },
  { key: "status" },
  { key: "tags", derive: tagNamesCell },
  { key: "created_at" },
  { key: "id" },
];

/**
 * §4.14's deals (P3-E2 PR E2-1, D-248 through §4.1's rules). The two money
 * keys are §7 omissions, written only when a row carries them; a deal whose
 * value nobody estimated carries `null`, which is an empty cell and never a
 * zero (D-312 ruling 7). `customer_id` rides beside `customer_name` by §4.7's
 * own reason for leaving it out of the orders file: there the customer's name
 * names the customer, and here the name is null to a reader the customer door
 * refuses (D-312 ruling 21), so for that reader the id is the only column
 * that names the customer (spec 09 §4.14 lists these columns).
 * `next_activity` is an object and stays out; `needs_next_step` says the flag.
 */
export const OPPORTUNITY_EXPORT_COLUMNS: readonly ExportColumn<Opportunity>[] = [
  { key: "opportunity_no" },
  { key: "title" },
  { key: "customer_name" },
  { key: "customer_id" },
  { key: "stage_code" },
  { key: "probability_pct" },
  { key: "status" },
  { key: "currency" },
  { key: "expected_value_minor", omittable: true },
  { key: "weighted_value_minor", omittable: true },
  { key: "expected_close_month" },
  { key: "close_month_passed" },
  { key: "idle_days" },
  { key: "rotting" },
  { key: "needs_next_step" },
  { key: "lost_reason_code" },
  { key: "won_via" },
  { key: "won_quote_no" },
  { key: "won_order_no" },
  { key: "owner_user_id" },
  { key: "owner_name" },
  { key: "closed_at" },
  { key: "created_at" },
  { key: "updated_at" },
  { key: "id" },
];

/** One wire value as a cell: booleans spelled, null and absent both empty. */
function wireCell(value: Scalar): string | number | null | undefined {
  if (typeof value === "boolean") return value ? "true" : "false";
  return value;
}

function cellOf<T>(column: ExportColumn<T>, row: T): Scalar {
  if (column.derive !== undefined) return column.derive(row);
  return row[column.key] as Scalar;
}

function serialize<T>(columns: readonly ExportColumn<T>[], rows: readonly T[]): SerializedList {
  const written = columns.filter(
    (column) =>
      column.omittable !== true || rows.some((row) => row[column.key as keyof T] !== undefined),
  );
  return {
    header: written.map((column) => column.key),
    body: rows.map((row) => written.map((column) => wireCell(cellOf(column, row)))),
    // Nothing was withheld from an empty file; the sentence would be about
    // rows that do not exist.
    moneyOmitted: rows.length > 0 && written.length < columns.length,
  };
}

export function ordersCsv(rows: readonly SalesOrder[]): SerializedList {
  return serialize(ORDER_EXPORT_COLUMNS, rows);
}

export function quotesCsv(rows: readonly Quote[]): SerializedList {
  return serialize(QUOTE_EXPORT_COLUMNS, rows);
}

export function customersCsv(rows: readonly CustomerListRow[]): SerializedList {
  return serialize(CUSTOMER_EXPORT_COLUMNS, rows);
}

export function opportunitiesCsv(rows: readonly Opportunity[]): SerializedList {
  return serialize(OPPORTUNITY_EXPORT_COLUMNS, rows);
}

/** The whole file: the header, the provenance row as line 2, then the body. */
export function exportDocument(
  list: SerializedList,
  provenance: readonly string[],
  convention: CsvConvention,
): string {
  // No `decimalHeaders`: D-289 ruling 3 made every sales cell a WIRE value, and
  // the only numbers among them are integer minor units beside a currency
  // column — which read as numbers in every locale, which is why that ruling
  // chose them. There is no decimal quantity here to re-punctuate.
  return toCsv(list.header, [honestyRow(provenance, list.header.length), ...list.body], {
    convention,
  });
}

export type ExportList = "orders" | "quotes" | "customers" | "opportunities";

/**
 * Is this reader narrowed by customer ASSIGNMENT? True only when every role
 * the reader holds that grants `sales.read` is `sales` — the one role the
 * service narrows (D-224: assigned customers, plus owned rows on the orders
 * and quotes reads; `provenanceParts` words each). Any other reading role (an admin,
 * finance, a planner) and the scope phrase says only "rows this account can
 * read": the file never claims a completeness, or a narrowness, the role list
 * does not prove.
 */
export function isAssignedOnlyReader(roles: RoleInput): boolean {
  let sales = false;
  // The service keys the narrowing on the role's LINEAGE (`sales-reach.ts`),
  // so a tenant's renamed clone of `sales` is assigned-only too (D-586); a
  // feed without lineages reads the literal codes, which is the same for seed
  // roles.
  for (const role of lineageCodesOf(roles)) {
    // A role this mirror does not know (a bespoke role cloned from nothing)
    // may read every row on the server, so it disproves "assigned only" rather
    // than being skipped — the claim must be provable, never assumed (review
    // S2).
    const grants = SALES_ROLE_GRANTS[role];
    if (grants === undefined) return false;
    if (!grants.includes("sales.read")) continue;
    if (role !== "sales") return false;
    sales = true;
  }
  return sales;
}

/** The facts line 2 of an export states, in order (D-289 ruling 13). */
export interface ProvenanceFacts {
  /** The tenant's display name; left out while the feed has not answered. */
  readonly tenantName: string | undefined;
  readonly list: ExportList;
  readonly assignedOnly: boolean;
  /** Already-localized "label: value" phrases, as the screen names them. */
  readonly filters: readonly string[];
  /** The wire sort token the walk sent (`-confirmed_at`). */
  readonly order: string;
  /** The export instant, ISO. */
  readonly exportedAt: string;
  readonly count: number;
  /** How the file is punctuated, so the row can say so (D-294 ruling 7). */
  readonly convention: CsvConvention;
  readonly capped: boolean;
  readonly moneyOmitted: boolean;
}

/** Line 2's phrases, localized. The sort names the wire column, which is also
 * the file's header for it. Empty phrases are dropped by `honestyRow`.
 *
 * The narrowed reader's scope phrase is per list, because the server's read
 * arms differ: the customers list holds assigned customers only, while the
 * orders and quotes lists hold the assigned customers' rows AND the rows the
 * reader owns (the owner arm — a rep keeps reading the orders and quotes they
 * own after being unassigned from the customer). Saying "assigned customers
 * only" on those two would claim a narrower file than the one written. */
export function provenanceParts(t: TFunction, facts: ProvenanceFacts): string[] {
  const descending = facts.order.startsWith("-");
  const column = descending ? facts.order.slice(1) : facts.order;
  return [
    facts.tenantName ?? "",
    t(`sales.export.list.${facts.list}`),
    facts.assignedOnly
      ? t(`sales.export.scopeAssigned.${facts.list}`)
      : t("sales.export.scopeReadable"),
    facts.filters.length > 0
      ? t("sales.export.filters", { filters: facts.filters.join("; ") })
      : t("sales.export.noFilters"),
    descending ? t("sales.export.sortDesc", { column }) : t("sales.export.sortAsc", { column }),
    t("sales.export.exportedAt", { at: facts.exportedAt }),
    facts.capped
      ? t("sales.export.capped", { count: facts.count })
      : t("sales.export.complete", { count: facts.count }),
    facts.moneyOmitted ? t("sales.export.moneyOmitted") : "",
    facts.convention.delimiter === ";"
      ? t("sales.export.punctuationSemicolonComma")
      : t("sales.export.punctuationCommaDot"),
  ];
}

/** `sales-orders_kazkabel_2026-09-18.csv` — the tenant's subdomain and the
 * tenant's business date. A subdomain not yet known is left out rather than
 * guessed. */
export function exportFilename(
  list: ExportList,
  subdomain: string | undefined,
  tenantToday: string,
): string {
  const parts = [`sales-${list}`];
  if (subdomain !== undefined && subdomain.length > 0) parts.push(subdomain);
  parts.push(tenantToday);
  return `${parts.join("_")}.csv`;
}

/** "Label: value", the way a filter row reads to the person who set it. */
function phrase(label: string, value: string): string {
  return `${label}: ${value}`;
}

/** §4.7's filters as the order list names them. `customer` and `facility`
 * are the NAMES the screen shows (or the id while a name is unresolved). */
export function ordersFilters(
  t: TFunction,
  filters: {
    status: string;
    customer: string | undefined;
    facility: string | undefined;
    /** The import's file name, when the list is narrowed to one import. */
    importFile?: string | undefined;
    /** The at-risk toggle is on (P3-J J-3). */
    atRisk?: boolean | undefined;
  },
): string[] {
  const out: string[] = [];
  if (filters.status.length > 0) {
    out.push(phrase(t("sales.orders.filterStatus"), t(`sales.orders.status.${filters.status}`)));
  }
  if (filters.atRisk === true) out.push(t("sales.orders.filterAtRisk"));
  if (filters.customer !== undefined) {
    out.push(phrase(t("sales.orders.filterCustomer"), filters.customer));
  }
  if (filters.facility !== undefined) {
    out.push(phrase(t("sales.orders.filterFacility"), filters.facility));
  }
  if (filters.importFile !== undefined) {
    out.push(phrase(t("sales.orders.filterImport"), filters.importFile));
  }
  return out;
}

/** §4.5's filters as the quote book names them. */
export function quotesFilters(
  t: TFunction,
  filters: {
    status: string;
    origin: string;
    owner: string | undefined;
    customer: string | undefined;
    createdFrom: string;
    createdTo: string;
    offListOnly: boolean;
  },
): string[] {
  const out: string[] = [];
  if (filters.status.length > 0) {
    out.push(phrase(t("sales.quotes.filterStatus"), t(`sales.quoteStatus.${filters.status}`)));
  }
  if (filters.origin.length > 0) {
    out.push(phrase(t("sales.quotes.filterOrigin"), t(`sales.quotes.origin.${filters.origin}`)));
  }
  if (filters.owner !== undefined) {
    out.push(phrase(t("sales.quotes.filterOwner"), filters.owner));
  }
  if (filters.customer !== undefined) {
    out.push(phrase(t("sales.quotes.filterCustomer"), filters.customer));
  }
  if (filters.createdFrom.length > 0) {
    out.push(phrase(t("sales.quotes.filterFrom"), filters.createdFrom));
  }
  if (filters.createdTo.length > 0) {
    out.push(phrase(t("sales.quotes.filterTo"), filters.createdTo));
  }
  if (filters.offListOnly) out.push(t("sales.quotes.filterOffList"));
  return out;
}

/** §4.1's filters as the customer book names them. `q` is the debounced search
 * the list was actually asked with, not the box's latest keystroke. */
export function customersFilters(
  t: TFunction,
  filters: {
    q: string;
    tier: string;
    status: string;
    owner: string | undefined;
    creditHold: string;
    /** The discipline flag (P3-E2 PR E2-1); absent is false. */
    needsNextStep?: boolean;
    /** The Tag filter's tag NAME as the bar shows it (E2-2a1), or undefined
     * when no tag filters the book. The caller holds the press while the
     * name is still loading, so a file never names a filter by a raw id. */
    tag?: string | undefined;
  },
): string[] {
  const out: string[] = [];
  if (filters.q.length > 0) out.push(phrase(t("sales.customers.search"), filters.q));
  if (filters.tier.length > 0) {
    out.push(phrase(t("sales.customers.filterTier"), t(`sales.tier.${filters.tier}`)));
  }
  if (filters.status.length > 0) {
    out.push(
      phrase(
        t("sales.customers.filterStatus"),
        filters.status === "archived"
          ? t("sales.customers.statusArchived")
          : t("sales.customers.statusActive"),
      ),
    );
  }
  if (filters.owner !== undefined) {
    out.push(phrase(t("sales.customers.filterOwner"), filters.owner));
  }
  if (filters.creditHold.length > 0) {
    out.push(
      phrase(
        t("sales.customers.filterCreditHold"),
        filters.creditHold === "true"
          ? t("sales.customers.creditHoldOn")
          : t("sales.customers.creditHoldOff"),
      ),
    );
  }
  if (filters.needsNextStep === true) out.push(t("sales.customers.filterNeedsNextStep"));
  if (filters.tag !== undefined) out.push(phrase(t("sales.customers.filterTag"), filters.tag));
  return out;
}

/** §4.14's deal-list filters as the list names them. `stage`, `owner` and
 * `customer` are the NAMES the screen shows (or the id while a name is
 * unresolved); `status`, `lostReason` and the two flags arrive already worded. */
export function opportunitiesFilters(
  t: TFunction,
  filters: {
    readonly q: string;
    readonly status: string | undefined;
    readonly stage: string | undefined;
    readonly owner: string | undefined;
    readonly customer: string | undefined;
    readonly closeFrom: string;
    readonly closeTo: string;
    readonly rotting: boolean;
    readonly needsNextStep: boolean;
    readonly lostReason: string | undefined;
  },
): string[] {
  const out: string[] = [];
  if (filters.q.length > 0) out.push(phrase(t("sales.pipeline.list.search"), filters.q));
  if (filters.status !== undefined) {
    out.push(phrase(t("sales.pipeline.list.filterStatus"), filters.status));
  }
  if (filters.stage !== undefined) {
    out.push(phrase(t("sales.pipeline.list.filterStage"), filters.stage));
  }
  if (filters.owner !== undefined) {
    out.push(phrase(t("sales.pipeline.filterOwner"), filters.owner));
  }
  if (filters.customer !== undefined) {
    out.push(phrase(t("sales.pipeline.filterCustomer"), filters.customer));
  }
  if (filters.closeFrom.length > 0) {
    out.push(phrase(t("sales.pipeline.list.filterCloseFrom"), filters.closeFrom));
  }
  if (filters.closeTo.length > 0) {
    out.push(phrase(t("sales.pipeline.list.filterCloseTo"), filters.closeTo));
  }
  if (filters.rotting) out.push(t("sales.pipeline.list.filterRotting"));
  if (filters.needsNextStep) out.push(t("sales.pipeline.list.filterNeedsNextStep"));
  if (filters.lostReason !== undefined) {
    out.push(phrase(t("sales.pipeline.list.filterLostReason"), filters.lostReason));
  }
  return out;
}
